import { useRef, useEffect } from "react";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Link2,
  Image as ImageIcon,
  Pilcrow,
} from "lucide-react";

const Btn = ({ onClick, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft hover:bg-sand-deep hover:text-ink transition-colors"
  >
    {children}
  </button>
);

// Dependency-free rich text editor (React 19 safe). Uses contentEditable +
// document.execCommand for the toolbar. Emits HTML via onChange.
export const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = sanitizeHtml(value);
    }
    // initialise once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current ? sanitizeHtml(ref.current.innerHTML) : "");
  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };
  const block = (tag) => exec("formatBlock", tag);
  const addLink = () => {
    const url = window.prompt("Link URL (https://…):");
    if (url) exec("createLink", url);
  };
  const addImage = () => {
    const url = window.prompt("Image URL (https://…):");
    if (!url) return;
    const alt = window.prompt("Alt text (describe the image for SEO & accessibility):") || "";
    document.execCommand(
      "insertHTML",
      false,
      `<img src="${url}" alt="${alt.replace(/"/g, "&quot;")}" style="max-width:100%" />`
    );
    emit();
  };

  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-sand/60 px-2 py-1.5">
        <Btn title="Heading 2" testid="rte-h2" onClick={() => block("h2")}><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Heading 3" testid="rte-h3" onClick={() => block("h3")}><Heading3 className="h-4 w-4" /></Btn>
        <Btn title="Paragraph" testid="rte-p" onClick={() => block("p")}><Pilcrow className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-border mx-1" />
        <Btn title="Bold" testid="rte-bold" onClick={() => exec("bold")}><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" testid="rte-italic" onClick={() => exec("italic")}><Italic className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-border mx-1" />
        <Btn title="Bulleted list" testid="rte-ul" onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" testid="rte-ol" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></Btn>
        <span className="w-px h-5 bg-border mx-1" />
        <Btn title="Insert link" testid="rte-link" onClick={addLink}><Link2 className="h-4 w-4" /></Btn>
        <Btn title="Insert image" testid="rte-image" onClick={addImage}><ImageIcon className="h-4 w-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid="rte-body"
        className="article-prose min-h-[320px] max-h-[560px] overflow-y-auto px-5 py-4 outline-none text-ink-soft leading-relaxed"
      />
    </div>
  );
};
